import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import { useCart } from '../hooks/useCart';
import { useAuth } from '../hooks/useAuth';
import { Banner, EmptyState } from '../components/States';
import type { CotacaoSacola, Order } from '../api/types';
import {
  itemDaSacola,
  trackAddShippingInfo,
  trackBeginCheckout,
  trackCheckoutPriceChange,
  trackRemoveFromCart,
  trackViewCart,
} from '../lib/tracking';
import { AoAparecer } from '../lib/tracking/react';
import { abrirAbaEmBranco, enviarAoAtendimento } from '../lib/whatsapp';

const brl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/**
 * A sacola antes de virar pedido.
 *
 * O total mostrado é a conta do servidor (com o desconto de kit quando há faca
 * e luva juntas); enquanto ela não chega, vale a soma dos preços vistos ao
 * adicionar. O fechamento refaz a conta, e a tela avisa quando ela mudou.
 */
export function CartPage() {
  const { linhas, total, remover, limpar } = useCart();
  const { user, loading, loginUrl } = useAuth();
  const navigate = useNavigate();
  const [obs, setObs] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [fechando, setFechando] = useState(false);
  /**
   * Pedido criado que voltou diferente da sacola, esperando a palavra do cliente.
   *
   * Fica em estado, e não num window.confirm: no celular a caixa nativa é
   * bloqueante, aparece descolada da página e não cabe explicar nela o que
   * mudou. Aqui o aviso fica na tela, ao lado dos itens que ele descreve.
   */
  const [pendente, setPendente] = useState<{
    code: string;
    aviso: string;
  } | null>(null);
  /** O pedido que espera a palavra do cliente, só para o rastreamento ter os itens. */
  const pedidoPendente = useRef<Order | null>(null);
  /** A conta do servidor para a sacola atual. */
  const [cotacao, setCotacao] = useState<CotacaoSacola | null>(null);
  /**
   * O cupom digitado, guardado no navegador.
   *
   * O cliente costuma sair da sacola para buscar o código com o streamer e
   * voltar; perder o que ele já digitou é o jeito mais fácil de perder a venda.
   */
  const [cupom, setCupom] = useState(() => {
    try {
      return localStorage.getItem('dms:cupom') ?? '';
    } catch {
      return '';
    }
  });

  // O desconto de kit depende do par inteiro e da cotação que mora no servidor,
  // então a sacola pergunta a conta a cada mudança em vez de somar sozinha.
  // A espera curta junta cliques seguidos em "Remover" numa pergunta só.
  const chaveSacola = linhas.map((l) => l.listingId).join(',');
  useEffect(() => {
    if (!user || linhas.length === 0) {
      setCotacao(null);
      return;
    }
    let vivo = true;
    const t = window.setTimeout(() => {
      void api
        .cotarSacola(
          linhas.map((l) => ({
            templateId: l.templateId,
            listingId: l.listingId,
          })),
          cupom.trim(),
        )
        .then((c) => vivo && setCotacao(c))
        // Sem a conta do servidor a sacola segue com a soma local: o fechamento
        // confere de qualquer jeito.
        .catch(() => vivo && setCotacao(null));
    }, 250);
    return () => {
      vivo = false;
      window.clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chaveSacola, user?.id, cupom]);

  function digitarCupom(valor: string) {
    const limpo = valor.toUpperCase().replace(/\s+/g, '');
    setCupom(limpo);
    try {
      if (limpo) localStorage.setItem('dms:cupom', limpo);
      else localStorage.removeItem('dms:cupom');
    } catch {
      // Armazenamento bloqueado: o cupom vale nesta tela e some ao sair.
    }
  }

  const linhaCotada = (listingId: string) => cotacao?.linhas.find((c) => c.listingId === listingId) ?? null;
  const totalEsperado = cotacao?.totalBrl ?? total;

  /**
   * Manda o pedido recém-criado ao atendimento e abre o WhatsApp.
   *
   * A aba já chega aberta, vinda do clique: o navegador bloqueia abrir aba
   * depois de esperar a API, então ela nasce em branco no clique e só recebe o
   * endereço do WhatsApp aqui. Se o envio falhar, o pedido existe mesmo assim —
   * a tela dele oferece enviar de novo.
   */
  async function concluirFechamento(order: Order, aba: Window | null) {
    trackAddShippingInfo(order);
    await enviarAoAtendimento(order, aba);
    limpar();
    navigate(`/pedido/${order.code}`);
  }

  /** Segue com o pedido do jeito que o servidor devolveu. */
  async function seguirAssim(code: string) {
    const pedido = pedidoPendente.current;
    if (!pedido || pedido.code !== code) return;
    // Este clique é um gesto novo: a aba do WhatsApp abre aqui.
    const aba = abrirAbaEmBranco();
    pedidoPendente.current = null;
    setPendente(null);
    setFechando(true);
    await concluirFechamento(pedido, aba);
  }

  /** Desiste: cancela o pedido recém-criado e devolve a sacola intacta. */
  async function revisar(code: string) {
    setPendente(null);
    setFechando(true);
    try {
      await api.cancelarPedido(code, 'Divergência de preço ou disponibilidade no fechamento.');
      setErro('Pedido cancelado. Sua sacola continua aqui para você revisar as peças.');
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui cancelar o pedido. Fale com o atendimento.');
    } finally {
      setFechando(false);
    }
  }

  async function fechar() {
    trackBeginCheckout('cart', linhas.map(itemDaSacola), totalEsperado);
    // Aberta ainda dentro do clique, antes de qualquer espera: é o que faz o
    // navegador deixar o WhatsApp abrir quando o pedido ficar pronto.
    const aba = abrirAbaEmBranco();
    setFechando(true);
    setErro(null);
    try {
      const { order } = await api.criarPedido({
        kind: 'encomenda',
        items: linhas.map((l) => ({
          templateId: l.templateId,
          listingId: l.listingId,
        })),
        note: obs.trim() || undefined,
        // O servidor confere o cupom de novo: o que vale é a conta dele.
        cupom: cupom.trim() || undefined,
      });

      // O servidor reconfere preço e disponibilidade de cada anúncio, então o
      // pedido pode voltar com menos peças ou com outro valor. Seguir calado
      // para a tela do pedido é o pior desfecho: o cliente descobre a diferença
      // depois de achar que já estava fechado.
      //
      // Limitação conhecida: não existe endpoint de prévia, então o pedido já
      // nasceu quando esta conferência acontece. Por isso a recusa cancela o
      // pedido recém-criado e devolve a sacola intacta — sem isso ficaria um
      // pedido órfão em aberto no atendimento.
      const faltando = linhas.length - order.items.length;
      // Comparação em centavos. A sacola guarda o preço convertido sem arredondar
      // (7,4099…) e o servidor devolve já em centavos (7,41): comparados crus,
      // os dois diferiam sempre, e o cliente via "o total passou de R$ 7,41 para
      // R$ 7,41" em pedido que não tinha mudado nada.
      const centavos = (v: number) => Math.round(v * 100);
      // A referência é a conta que o cliente viu: a do servidor, já com o kit.
      const totalMudou = centavos(order.totalBrl) !== centavos(totalEsperado);
      const difere = totalMudou || faltando !== 0;
      if (difere) {
        const partes: string[] = [];
        if (faltando > 0) {
          partes.push(
            faltando === 1
              ? '1 peça saiu do ar e não entrou no pedido.'
              : `${faltando} peças saíram do ar e não entraram no pedido.`,
          );
        } else if (faltando < 0) {
          partes.push('O pedido voltou com mais itens do que a sacola.');
        }
        if (totalMudou) {
          partes.push(`O total passou de ${brl(totalEsperado)} para ${brl(order.totalBrl)}.`);
        }
        // O pedido só conta como etapa concluída se o cliente seguir: se ele
        // revisar, o pedido é cancelado, e contá-lo agora inflaria o funil.
        trackCheckoutPriceChange({
          pecasFora: Math.max(faltando, 0),
          antesBrl: totalEsperado,
          depoisBrl: order.totalBrl,
        });
        // Nada vai ao WhatsApp antes de o cliente ver a mudança e decidir.
        aba?.close();
        pedidoPendente.current = order;
        setPendente({ code: order.code, aviso: partes.join(' ') });
        setFechando(false);
        return;
      }

      await concluirFechamento(order, aba);
    } catch (e) {
      aba?.close();
      setErro(e instanceof ApiError ? e.message : 'Não consegui fechar o pedido.');
      setFechando(false);
    }
  }

  if (linhas.length === 0) {
    return (
      <EmptyState>
        {/* Link, e não <a href>: âncora comum recarrega o site inteiro. */}
        Sua sacola está vazia. <Link to="/all">Ver o catálogo</Link>
      </EmptyState>
    );
  }

  return (
    <div className="panel">
      <AoAparecer disparar={() => trackViewCart(linhas.map(itemDaSacola))} />
      <h2>Sua sacola</h2>
      <p className="muted">
        Cada linha é uma peça específica, com o float e o pattern que você escolheu. No fechamento conferimos se ela
        continua à venda — anúncio é único e pode sair do ar a qualquer momento.
      </p>

      {erro && <Banner kind="error">{erro}</Banner>}

      {pendente && (
        <Banner kind="warn">
          {pendente.aviso}
          <div className="actions-row">
            <button className="btn primary small" onClick={() => void seguirAssim(pendente.code)} disabled={fechando}>
              Seguir assim
            </button>
            <button className="btn ghost small" onClick={() => void revisar(pendente.code)} disabled={fechando}>
              Revisar a sacola
            </button>
          </div>
        </Banner>
      )}

      {/*
        data-label alimenta o modo cartão do celular: abaixo de 560px a tabela
        vira uma pilha de linhas rotuladas (ver styles.css). Sem isso a sacola
        rolava de lado e o botão Remover ficava fora da tela.
      */}
      <div className="table-wrap">
        <table className="admin-table cards-no-mobile">
          <thead>
            <tr>
              <th>Skin</th>
              <th>Float</th>
              <th>Pattern</th>
              <th>Preço</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {linhas.map((l) => {
              const cotada = linhaCotada(l.listingId);
              return (
                <tr key={l.listingId}>
                  <td data-label="Skin">
                    <div className="cli">
                      {/* A miniatura abre a inspeção no jogo: quem está conferindo a
                        sacola antes de fechar quer olhar a peça, e o link do
                        anúncio já vem guardado com ela. */}
                      {l.iconUrl &&
                        (l.inspectUrl ? (
                          <a href={l.inspectUrl} title="Inspecionar esta peça no CS2">
                            <img src={l.iconUrl} alt="" style={{ borderRadius: 6 }} />
                          </a>
                        ) : (
                          <img src={l.iconUrl} alt="" style={{ borderRadius: 6 }} />
                        ))}
                      <div>
                        <b>{l.name}</b>
                        {cotada?.kit && <span className="sacola-kit">kit</span>}
                        {l.exterior && <div className="dim">{l.exterior}</div>}
                        {l.inspectUrl && (
                          <a className="dim" href={l.inspectUrl} title="Inspecionar esta peça no CS2">
                            🔍 inspecionar no jogo
                          </a>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="dim" data-label="Float">
                    {l.float !== null ? l.float.toFixed(8) : '—'}
                  </td>
                  <td className="dim" data-label="Pattern">
                    {l.paintSeed ?? '—'}
                  </td>
                  <td data-label="Preço">
                    {cotada?.kit ? (
                      <span className="sacola-preco-kit">
                        <s className="dim">{brl(cotada.unitCheioBrl)}</s>
                        <b>{brl(cotada.unitBrl)}</b>
                      </span>
                    ) : (
                      <b>{brl(cotada?.unitBrl ?? l.unitBrl)}</b>
                    )}
                  </td>
                  <td>
                    <button
                      className="btn ghost small"
                      onClick={() => {
                        trackRemoveFromCart(itemDaSacola(l));
                        remover(l.listingId);
                      }}
                    >
                      Remover
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="sacola-cupom">
        <label className="field">
          <span>Cupom de desconto</span>
          <input
            id="sacola-cupom"
            className="input"
            placeholder="Código do streamer"
            value={cupom}
            maxLength={24}
            autoComplete="off"
            onChange={(e) => digitarCupom(e.target.value)}
          />
        </label>
        {cotacao?.cupom && (
          <span className="sacola-cupom-ok">
            Cupom <b>{cotacao.cupom.code}</b> aplicado.
          </span>
        )}
        {cotacao?.cupomRecusado && <span className="sacola-cupom-nao">{cotacao.cupomRecusado}</span>}
      </div>

      {cotacao?.cupom && (
        <div className="sacola-desconto-kit">
          <span>
            Cupom {cotacao.cupom.code}
            <small className="dim"> conferido de novo no fechamento</small>
          </span>
          <b>−{brl(cotacao.cupom.descontoBrl)}</b>
        </div>
      )}

      {cotacao && cotacao.economiaKitBrl > 0 && (
        <div className="sacola-desconto-kit">
          {/* O desconto só existe com o par: tirar uma das peças some com ele,
              e dizer isso aqui evita a surpresa de ver o total subir. */}
          <span>
            Desconto de kit
            {cotacao.pares > 1 ? ` (${cotacao.pares} pares)` : ''}
            <small className="dim"> vale enquanto faca e luva estiverem juntas</small>
          </span>
          <b>−{brl(cotacao.economiaKitBrl)}</b>
        </div>
      )}

      <div className="total-box">
        <span>Total da sacola</span>
        {cotacao && cotacao.economiaKitBrl > 0 && <s className="dim">{brl(cotacao.totalCheioBrl)}</s>}
        <b>{brl(totalEsperado)}</b>
        <small>entrega no trade link do seu cadastro</small>
      </div>

      <label className="field">
        <span>Observação (opcional)</span>
        <input
          value={obs}
          onChange={(e) => setObs(e.target.value)}
          placeholder="algo que o atendimento precise saber"
        />
      </label>

      {loading ? null : !user ? (
        <Banner kind="warn">
          Entre com a Steam para fechar o pedido. <a href={loginUrl}>Entrar agora</a>
        </Banner>
      ) : user.needsOnboarding ? (
        <Banner kind="warn">
          {/* Link, e não <a href>: âncora comum recarrega o site inteiro. */}
          Falta o seu trade link. <Link to="/cadastro">Completar cadastro</Link>
        </Banner>
      ) : null}

      <div className="actions-row">
        <button
          className="btn primary"
          onClick={() => void fechar()}
          disabled={fechando || !user || user.needsOnboarding}
        >
          {fechando ? 'Enviando ao atendimento…' : 'Fechar pedido e enviar no WhatsApp'}
        </button>
        <button className="btn ghost" onClick={limpar} disabled={fechando}>
          Esvaziar sacola
        </button>
      </div>
    </div>
  );
}
